import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { omit } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  getDayEffectiveEndPosition,
  hasHotelLocationConflict,
} from '@koob/utils';
import { ProgramAccommodationTypeEnum } from '@koob/enums';
import { pushEvent } from '@koob/google-tag-manager';
import { GTM_EVENTS } from '@koob/google-tag-manager/src/events';
import { assistantSessionFor } from 'components/Assistant/assistantSessions';
import {
  useDays,
  useResetDaySelection,
  useSelectedDay,
  useSetFocusDay,
  useSetLastAddedExperienceId,
  useSetListMode,
  useSetLoading,
  useSetTripFilters,
  useTripFilters,
  useTripStore,
} from '@koob/trip-designer';
import { getExperience } from '../../../api/experiences';
import { getHotel } from '../../../api/hotels';
import { useLanguage, useSnack } from '../../../hooks';
import { decodeId } from '../../../utils';
import { prefillDays } from './prefillDays';
import { countAvailableBlankDays, insertBlankDays } from './insertBlankDays';

/**
 * Puts a hotel or an experience from the list on the selected day. An
 * experience longer than the free days waits for confirmation first
 * (`overlapingExperience`).
 */
export function useAddToTrip() {
  const { t } = useTranslation('tripDesigner');
  const { notify } = useSnack();
  const { lang } = useLanguage();
  const tripFilters = useTripFilters();
  const setLoading = useSetLoading();
  const setTripFilters = useSetTripFilters();
  const selectedDay = useSelectedDay();
  const resetDaySelection = useResetDaySelection();
  const setLastAddedExperienceId = useSetLastAddedExperienceId();
  const setFocusDay = useSetFocusDay();
  const setListMode = useSetListMode();

  const days = useDays();
  const experiencesMap = useTripStore(state => state.experiences);
  const hotelsMap = useTripStore(state => state.hotels);
  const updateDays = useTripStore(state => state.updateDays);
  const setExperienceInStore = useTripStore(state => state.setExperience);
  const setHotelInStore = useTripStore(state => state.setHotel);

  const [overlapingExperience, setOverlapingExperience] = useState(null);

  const addHotel = hotel => {
    pushEvent(GTM_EVENTS.HOTEL_ADDED, {
      hotel_id: hotel?.id,
      context: 'trip',
      assistant_session_id: assistantSessionFor('hotel', hotel?.id),
    });
    const newDays = prefillDays(days, selectedDay + 1);
    const currentDay = newDays?.[selectedDay];
    const currentExperience = currentDay?.experienceId
      ? experiencesMap?.[currentDay.experienceId]
      : null;
    const experienceDayIdx = currentDay?.experienceDayIndex
      ? currentDay.experienceDayIndex - 1
      : 0;
    const programDay = currentExperience?.programs?.[experienceDayIdx];

    const isIncludedHotel =
      programDay?.isIncludedAccomodation &&
      (programDay?.accomodationHotel?.id === hotel?.id?.toString() ||
        programDay?.alternativeHotels?.some(
          ({ id }) => id === hotel?.id?.toString(),
        ));

    const decodedHotelId = decodeId(hotel.id);

    // Only update hotel if it doesn't exist or if it's an included hotel
    // For existing hotels, preserve room selection to avoid wiping data for other groups
    const existingHotel = hotelsMap[decodedHotelId];

    if (!existingHotel || isIncludedHotel) {
      // Extract stale pricing/room data
      const hotelWithoutPricing = omit(hotel, [
        'selectedRoomKey',
        'roomsAvailableData',
      ]);

      // Add hotel to hotels map
      setHotelInStore(decodedHotelId, {
        ...hotelWithoutPricing,
        id: decodedHotelId,
        isIncluded: isIncludedHotel,
        filters: undefined,
      });
    } else if (
      hotel.minimumStay != null &&
      hotel.minimumStay !== existingHotel.minimumStay
    ) {
      // The hotel is otherwise left alone to preserve the room selection of existing
      // groups. Its minimum stay is refreshed all the same: it depends on the dates
      // just searched, and a store rehydrated from localStorage carries a pruned
      // entry, so without this the day row could never explain a too-short stay.
      // Guarded on a real change: duplicating a day calls this with the STORE object
      // itself, and rewriting it there would swap the hotel's identity for nothing,
      // invalidating the group's memos and its in-flight room availability query.
      setHotelInStore(decodedHotelId, {
        ...existingHotel,
        minimumStay: hotel.minimumStay,
      });
    }

    // If the next consecutive day already has the same hotel, we're extending the group
    // backward. Transfer the room key from the old first day so auto-select can restore
    // the same room type for the new (longer) stay.
    const nextDayWithSameHotel =
      newDays[selectedDay + 1]?.hotelId === decodedHotelId
        ? newDays[selectedDay + 1]
        : null;

    // Update day to reference the hotel
    newDays[selectedDay] = {
      ...currentDay,
      hotelId: decodedHotelId,
      hotelIsIncluded: isIncludedHotel,
      roomsComposition: nextDayWithSameHotel?.roomsComposition ?? null,
      ...(nextDayWithSameHotel?.selectedRoom?.roomKey
        ? { hotelSelectedRoomKey: nextDayWithSameHotel.selectedRoom.roomKey }
        : {}),
    };

    // Clear orphaned room selection from the old first day (now non-first in group)
    if (nextDayWithSameHotel) {
      newDays[selectedDay + 1] = {
        ...newDays[selectedDay + 1],
        selectedRoom: null,
        hotelPricing: null,
        hotelSelectedRoomKey: null,
        roomsComposition: null,
      };
    }

    updateDays(newDays);

    const hotelForConflictCheck = existingHotel || hotel;

    if (
      hasHotelLocationConflict({
        hotel: hotelForConflictCheck,
        program: programDay,
        endPosition: getDayEffectiveEndPosition({
          days: newDays,
          dayIndex: selectedDay,
          experiencesMap,
          program: programDay,
        }),
      })
    ) {
      notify(t('compose.hotelLocationMismatch'), { type: 'error' });
    }

    setListMode('map');
    setFocusDay(selectedDay);
    resetDaySelection();
  };

  const addExperience = async (experienceId, insertMissingDays = 0) => {
    pushEvent(GTM_EVENTS.EXPERIENCE_ADDED, {
      experience_id: experienceId,
      context: 'trip',
      assistant_session_id: assistantSessionFor('experience', experienceId),
    });
    setLoading(true);

    const experienceData = await getExperience({
      experienceId,
      locale: tripFilters?.locale ?? lang,
    });
    const experience = experienceData?.data;

    let currentDays = [...days];
    if (insertMissingDays > 0) {
      insertBlankDays(currentDays, selectedDay, insertMissingDays);
    }

    const availableBlankDays = countAvailableBlankDays(
      currentDays,
      selectedDay,
    );

    const missingDays = Math.max(
      0,
      experience.durationDays - availableBlankDays,
    );

    if (missingDays > 0 && insertMissingDays === 0 && currentDays.length > 0) {
      setOverlapingExperience({
        experienceId,
        programName: experience.name,
        duration: experience.durationDays,
        availableDays: availableBlankDays,
        missingDays,
      });
      setLoading(false);
      return;
    }

    const newDays = prefillDays(
      currentDays,
      selectedDay + experience.durationDays,
    );

    // Add experience to experiences map
    setExperienceInStore(experience.id, {
      ...experience,
      selectedExtras: [],
    });
    const hotelsForConflictCheck = { ...hotelsMap };

    for (let i = 0; i < Math.max(experience.durationDays, 1); i++) {
      const programDay = experience?.programs?.[i];
      const currentDay = newDays[selectedDay + i];
      let existingHotelId = null;

      // Only preserve existing hotel if accommodation is needed
      // Don't preserve if night_transfer or no_accomodation
      const shouldPreserveHotel =
        programDay?.accomodationType !==
          ProgramAccommodationTypeEnum.NIGHT_TRANSFER &&
        programDay?.accomodationType !==
          ProgramAccommodationTypeEnum.NO_ACCOMMODATION;

      const isPreservingExistingHotel =
        shouldPreserveHotel &&
        !programDay.isIncludedAccomodation &&
        !!currentDay?.hotelId;

      if (shouldPreserveHotel) {
        // Prefer the hotel defined in the program, fallback to the existing day's hotel
        existingHotelId = programDay.isIncludedAccomodation
          ? programDay?.accomodationHotel?.id
          : (currentDay?.hotelId ?? null);
      }

      newDays[selectedDay + i] = {
        id: uuidv4(),
        dayIndex: selectedDay + i + 1,
        experienceId: experience.id,
        experienceDayIndex: i + 1,
        hotelId: existingHotelId,
        experiencePricing: null,
        hotelPricing: isPreservingExistingHotel
          ? (currentDay?.hotelPricing ?? null)
          : null,
        hotelBookedOnMyOwn: isPreservingExistingHotel
          ? (currentDay?.hotelBookedOnMyOwn ?? false)
          : false,
        selectedRoom: isPreservingExistingHotel
          ? (currentDay?.selectedRoom ?? null)
          : null,
        selectedExtras: [],
      };

      const program = experience?.programs?.[i];

      if (program?.accomodationHotel && program?.isIncludedAccomodation) {
        const hotel = await getHotel({
          hotelId: program?.accomodationHotel?.id,
        });
        const decodedHotelId = decodeId(hotel?.data?.id);
        const includedHotel = {
          ...hotel?.data,
          id: decodedHotelId,
          isIncluded: true,
        };

        // Add hotel to hotels map
        setHotelInStore(decodedHotelId, includedHotel);
        hotelsForConflictCheck[decodedHotelId] = includedHotel;

        // Reference hotel in day (overrides manually selected hotel with included one)
        newDays[selectedDay + i].hotelId = decodedHotelId;
        newDays[selectedDay + i].hotelIsIncluded = true;
      }
    }

    if (newDays?.length > days?.length) {
      notify(t('compose.tripDurationExtended'));
    }

    updateDays(newDays);

    const durationDays = experience?.durationDays ?? 1;
    const hasNewConflicts = Array.from({ length: durationDays }).some(
      (_, i) => {
        const day = newDays[selectedDay + i];
        if (!day?.hotelId) return false;
        const hotel = hotelsForConflictCheck?.[day.hotelId];
        const exp =
          day.experienceId === experience.id
            ? experience
            : experiencesMap?.[day.experienceId];
        const prog = exp?.programs?.[(day.experienceDayIndex ?? 1) - 1];

        return hasHotelLocationConflict({
          hotel,
          program: prog,
          endPosition: getDayEffectiveEndPosition({
            days: newDays,
            dayIndex: selectedDay + i,
            experiencesMap,
            experience: exp,
            program: prog,
          }),
        });
      },
    );

    if (hasNewConflicts) {
      notify(t('compose.hotelLocationMismatch'), { type: 'error' });
    }

    setLoading(false);
    setListMode('map');
    setFocusDay(selectedDay);
    resetDaySelection();
    setLastAddedExperienceId(experience.id);

    // KD-3565 — the program is placed, so the search that found it has served
    // its purpose. Clearing it here rather than on the next day card keeps the
    // field honest in between, when no day is selected at all.
    setTripFilters(current => ({
      ...current,
      location: undefined,
    }));
  };

  const confirmAddExperience = () => {
    const { experienceId, missingDays } = overlapingExperience;
    setOverlapingExperience(null);
    addExperience(experienceId, missingDays);
  };

  return {
    addHotel,
    addExperience,
    overlapingExperience,
    cancelOverlap: () => setOverlapingExperience(null),
    confirmAddExperience,
  };
}
